import { FormControl, Grid, IconButton, Link, ListItem, MenuItem, Select, Typography, makeStyles } from "@material-ui/core";
import { AllEditorialTypes, EditorialTemplate, EditorialType } from "../../domain/domain";
import { useEffect, useState } from "react";
import { createEditorialTemplate, deleteEditorialTemplate, getEditorialTemplates, updateEditorialTemplate, updateEditorialTemplates } from "../../api/editorial";
import { useDispatch, useSelector } from "react-redux";
import { EditorialTemplatesActionType } from "../../reducers/editorialTemplates.reducer";
import { ApplicationState } from "../../reducers";
import { getEditorialContentTypeName } from "../../utils";
import ClearIcon from '@material-ui/icons/Clear';
import PencilIcon from '@material-ui/icons/Edit';
import AddEditorialTemplateDialog from "./AddEditorialTemplateDialog";
import { UiActionType } from "../../reducers/ui.reducer";
import { ConfirmationDialog } from "../Dialogs/ConfirmationDialog";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { reorder } from "../ComponentManagementPage/utils";


const useStyles = makeStyles(_ => ({
    draggingListItem: {
        background: 'rgb(235,235,235)'
    },
}));

const EditorialTemplatesList : React.FC = () => {

    const [selectedEditorialType, setSelectedEditorialType] = useState<EditorialType>(AllEditorialTypes[0]);
    const dispatch = useDispatch();
    const editorialTemplates = useSelector((app: ApplicationState) => app.editorialTemplates.editorialTemplates);
    const selectedEditorialTemplate = useSelector((app: ApplicationState) => app.editorialTemplates.selectedEditorialTemplate);
    const [addEditorialTemplateDialogOpen, setAddEditorialTemplateDialogOpen] = useState<boolean>(false);
    const [deleteEditorialConfirmationDialogOpen, setDeleteEditorialConfirmationDialogOpen] = useState<boolean>(false);
    const [editorialTemplateDialogEditorialTemplate, setEditorialTemplateDialogEditorialTemplate] = useState<EditorialTemplate | undefined>(undefined);
    const [addDialogMode, setAddDialogMode] = useState<'add' | 'edit'>('add');

    const classes = useStyles();

    useEffect(() => {
        getEditorialTemplates(selectedEditorialType)
            .then((response) => {
                dispatch({type: EditorialTemplatesActionType.EDITORIAL_TEMPLATES_LOADED, editorialTemplates: response.data});
                if (response.data.length > 0) {
                    dispatch({type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED, newSelectedEditorialTemplate: response.data[0]});
                } else {
                    dispatch({type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED, newSelectedEditorialTemplate: undefined});
                }
            }).catch((err) => {
                console.error(err);
            });
    }, [selectedEditorialType, dispatch]);

    if (editorialTemplates === undefined) {
        return <></>
    }

    const onDragEnd = ({ destination, source }: DropResult) => {
        if (!destination) {
            // dropped outside the list
            return;
        }
        const newItems = reorder(editorialTemplates, source.index, destination.index);
        for (let i = 0; i < newItems.length; i++) {
            newItems[i].templateOrder = i;
        }
        dispatch({type: EditorialTemplatesActionType.EDITORIAL_TEMPLATES_LOADED, editorialTemplates: newItems});
        updateEditorialTemplates(newItems).then(response => {
            dispatch({type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Order updated", severity: "success" } });
        }).catch(error => {
            dispatch({type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Could not update order", severity: "error" } });
            console.error(error);
        })
    };

    let sortedEditorialTemplates = [...editorialTemplates];
    sortedEditorialTemplates.sort((e1, e2) => e1.templateOrder < e2.templateOrder ? -1 : 1);

    return(
        <>
            <AddEditorialTemplateDialog open={addEditorialTemplateDialogOpen} editorialTemplate={editorialTemplateDialogEditorialTemplate} mode={addDialogMode} onOK={(title: string) => {
                setAddEditorialTemplateDialogOpen(false);
                let newEditorialTemplate: EditorialTemplate = {
                    id: 0,
                    title: title,
                    content: "",
                    editorialType: selectedEditorialType,
                    templateOrder: 0
                }
                if (addDialogMode === 'add') {
                    createEditorialTemplate(newEditorialTemplate).then(response => {
                        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Editorial Template created", severity: "success" } });
                        let updatedEditorials = [response.data, ...editorialTemplates];
                        for (let i = 0 ; i < updatedEditorials.length; i++) {
                            updatedEditorials[i].templateOrder = i;
                        }
                        dispatch({type: EditorialTemplatesActionType.EDITORIAL_TEMPLATES_LOADED, editorialTemplates: updatedEditorials});
                        dispatch({ type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED, newSelectedEditorialTemplate: response.data});
                    }).catch(error => {
                        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Could not create editorial template", severity: "error" } });
                        console.error(error);
                    })
                } else {
                    if (editorialTemplateDialogEditorialTemplate === undefined) {
                        return;
                    }
                    updateEditorialTemplate({...editorialTemplateDialogEditorialTemplate, title: title}).then(response => {
                        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Editorial Template updated", severity: "success" } });
                        dispatch({ type: EditorialTemplatesActionType.EDITORIAL_TEMPLATE_UPDATED, editorialTemplate: response.data});
                    }).catch(error => {
                        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Could not update editorial template", severity: "error" } });
                        console.error(error);
                    })
                }
            }} onCancel={() => {
                setAddEditorialTemplateDialogOpen(false);
            }} />
            <ConfirmationDialog
                open={deleteEditorialConfirmationDialogOpen}
                title=""
                contentText="Are you sure you want to delete this editorial template?"
                handleNo={() => {
                    setDeleteEditorialConfirmationDialogOpen(false);
                }}
                handleYes={() => {
                    if (selectedEditorialTemplate === undefined) {
                        return;
                    }
                    setDeleteEditorialConfirmationDialogOpen(false);
                    deleteEditorialTemplate(selectedEditorialTemplate).then(response => {
                        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Editorial Template deleted", severity: "success" } });
                        dispatch({ type: EditorialTemplatesActionType.EDITORIAL_TEMPLATE_DELETED, editorialTemplate: selectedEditorialTemplate});
                    }).catch(error => {
                        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Could not delete editorial template", severity: "error" } });
                        console.error(error);
                    })
                }}
            />
            <Grid container direction='column' className="flex-gap-1">
                <Grid item>
                    <FormControl
                    size="small"
                    style={{width: '100%'}}
                    >
                        <Select  variant="outlined" value={selectedEditorialType} onChange={(event) => {
                            setSelectedEditorialType(event.target.value as EditorialType);
                        }}>
                            {AllEditorialTypes.map((editorialType) => {
                                    return(
                                        <MenuItem value={editorialType} key={editorialType}>
                                            {getEditorialContentTypeName(editorialType)}
                                        </MenuItem>
                                    )
                                })
                            }
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item>
                    <Link type="button" style={{cursor: 'pointer'}} onClick={() => {
                        setAddDialogMode('add');
                        setEditorialTemplateDialogEditorialTemplate(undefined);
                        setAddEditorialTemplateDialogOpen(true);
                    }}>
                        Add editorial template
                    </Link>
                </Grid>
                <Grid item>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable-list">
                            {provided => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                    {sortedEditorialTemplates.map((item, index) => (
                                        <Draggable draggableId={"" + item.id} index={item.templateOrder} key={item.id}>
                                                {(provided, snapshot) => (
                                                    <ListItem
                                                        ref={provided.innerRef}
                                                        button
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={snapshot.isDragging ? classes.draggingListItem : ''}
                                                        onClick={() => {
                                                            dispatch({ type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED, newSelectedEditorialTemplate: item});
                                                        }}
                                                    >
                                                        <Grid container direction='row' wrap='nowrap' alignItems='center'>
                                                                <Grid item style={{flexGrow: 1}}>
                                                                    {item.title}
                                                                </Grid>
                                                                <Grid item>
                                                                    <IconButton size='small' onClick={() => {
                                                                        setAddDialogMode('edit');
                                                                        setEditorialTemplateDialogEditorialTemplate(item);
                                                                        setAddEditorialTemplateDialogOpen(true);
                                                                    }}>
                                                                        <PencilIcon style={{width: 16, height: 16}} />
                                                                    </IconButton>
                                                                </Grid>
                                                                <Grid item>
                                                                    <IconButton size='small' onClick={() => {
                                                                        setDeleteEditorialConfirmationDialogOpen(true);
                                                                    }}>
                                                                        <ClearIcon style={{width: 16, height: 16}} />
                                                                    </IconButton>
                                                                </Grid>
                                                            </Grid>
                                                    </ListItem>
                                                )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Grid>
                <Grid item>
                    <Typography style={{fontStyle: 'italic'}}>Drag and drop to reoder items.</Typography>
                </Grid>
            </Grid>
        </>
    )

}

export default EditorialTemplatesList;
